
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Ban,
  Building,
  Edit,
  FileSpreadsheet,
  MoreHorizontal,
  Trash,
  Users
} from 'lucide-react';
import type { Company } from '../hooks/useCompanyManagement';

interface CompaniesTableProps {
  companies: Company[];
  loading: boolean;
  onEdit: (company: Company) => void;
  onDelete: (companyId: string) => void;
  onViewUsers: (company: Company) => void;
  onSuspend: (company: Company) => void;
}

export default function CompaniesTable({
  companies,
  loading,
  onEdit,
  onDelete,
  onViewUsers,
  onSuspend
}: CompaniesTableProps) {
  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>Members</TableHead>
          <TableHead>Customers</TableHead>
          <TableHead>Contact</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {companies.map((company) => (
          <TableRow key={company.id}>
            <TableCell>
              <div className="flex items-center gap-2">
                {company.logo_url ? (
                  <img 
                    src={company.logo_url} 
                    alt={company.name}
                    className="h-8 w-8 rounded object-contain bg-gray-100"
                  />
                ) : (
                  <div className="h-8 w-8 rounded bg-gray-100 flex items-center justify-center">
                    <Building className="h-4 w-4 text-gray-500" />
                  </div>
                )}
                <div>
                  <div className="font-medium flex items-center gap-1">
                    {company.name}
                    {company.suspended && (
                      <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                        Suspended
                      </span>
                    )}
                    {!company.suspended && company.admin_count === 0 && (
                      <span className="ml-1 px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700">
                        No admins
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">{company.website}</div>
                </div>
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center">
                <Users className="mr-2 h-4 w-4 text-gray-500" />
                {company.member_count}
              </div>
            </TableCell>
            <TableCell>
              <div className="flex items-center">
                <FileSpreadsheet className="mr-2 h-4 w-4 text-gray-500" />
                {company.customer_count}
              </div>
            </TableCell>
            <TableCell>
              {company.email || "—"}
            </TableCell>
            <TableCell className="text-right">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onViewUsers(company)}>
                    <Users className="mr-2 h-4 w-4" />
                    View Users
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onSuspend(company)}
                    className={company.suspended ? 'text-green-600' : 'text-amber-600'}
                  >
                    <Ban className="mr-2 h-4 w-4" />
                    {company.suspended ? 'Unsuspend Company' : 'Suspend Company'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => onEdit(company)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit company
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => onDelete(company.id)}
                    className="text-red-600"
                  >
                    <Trash className="mr-2 h-4 w-4" />
                    Delete company
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </TableCell>
          </TableRow>
        ))}
        {companies.length === 0 && (
          <TableRow>
            <TableCell colSpan={5} className="text-center py-4">
              No companies found
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}
